import React, { ReactElement, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCourseV2PublishReadiness,
  fetchCourseV2Compliances,
  updatePlanningCourseV2Publish,
  selectCourseV2PublishReadinessByCourseID,
  selectCourseV2PublishReadinessStatusByCourseID,
  selectCourseV2PublishStatusByCourseID,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { PublishReadinessItem } from 'src/v3/core/types'

function ChecklistItem({ item }: { item: PublishReadinessItem }): ReactElement {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {item.approved ? (
        <CheckCircleIcon
          fontSize="small"
          sx={{ color: 'success.main', flexShrink: 0 }}
        />
      ) : (
        <CancelOutlinedIcon
          fontSize="small"
          sx={{
            color: item.status === 'Rejected' ? 'error.main' : 'text.disabled',
            flexShrink: 0,
          }}
        />
      )}
      <Typography
        variant="body2"
        color={item.approved ? 'text.primary' : 'text.secondary'}
        sx={{ flex: 1 }}
      >
        {item.field}
      </Typography>
      {!item.approved && item.status !== 'New' && (
        <Chip
          label={item.status}
          size="small"
          color={item.status === 'Rejected' ? 'error' : 'warning'}
          variant="outlined"
          sx={{ fontSize: '0.65rem', height: 18 }}
        />
      )}
    </Box>
  )
}

type Props = {
  courseID: string
  publishedStatus: 'published' | 'unpublished'
}

export default function CoursePublishChecklist({
  courseID,
  publishedStatus,
}: Props): ReactElement {
  let isPublished = publishedStatus === 'published'
  let [expanded, setExpanded] = useState(false)
  let dispatch = useAppDispatch()

  let readiness = useAppSelector(
    selectCourseV2PublishReadinessByCourseID(courseID),
  )
  let readinessStatus = useAppSelector(
    selectCourseV2PublishReadinessStatusByCourseID(courseID),
  )
  let publishStatus = useAppSelector(
    selectCourseV2PublishStatusByCourseID(courseID),
  )

  let isLoading =
    !readinessStatus ||
    readinessStatus === 'uninitialized' ||
    readinessStatus === 'pending'
  let isSaving = publishStatus === 'pending'

  useEffect(() => {
    if (!readinessStatus || readinessStatus === 'uninitialized') {
      dispatch(fetchCourseV2PublishReadiness(courseID))
    }
  }, [courseID, readinessStatus])

  let handlePublishToggle = () => {
    let newPublicationStatus: 'published' | 'unpublished' = isPublished
      ? 'unpublished'
      : 'published'
    // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
    dispatch(updatePlanningCourseV2Publish({ courseID, newPublicationStatus }))
      .unwrap()
      .then(() => {
        // Details refetch is driven by the slice invalidating details status
        // on publish; the planning dashboard owns that fetch.
        dispatch(fetchCourseV2Compliances())
      })
      .catch(() => {})
  }

  return (
    <Card variant="outlined" sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Publish Status
          </Typography>
          <Chip
            label={isPublished ? 'Published' : 'Draft'}
            color={isPublished ? 'success' : 'warning'}
            size="small"
          />
        </Box>
        <IconButton
          size="small"
          aria-label={
            expanded ? 'Collapse publish status' : 'Expand publish status'
          }
          aria-expanded={expanded}
          aria-controls="course-publish-checklist-body"
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((prev) => !prev)
          }}
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box id="course-publish-checklist-body" sx={{ pt: 2 }}>
          {!isPublished && (
            <>
              <Divider sx={{ mb: 1.5 }} />

              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} variant="text" height={20} />
                  ))}
                </Box>
              )}

              {!isLoading && readiness && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    mb: 1.5,
                  }}
                >
                  {readiness.items.map((item) => (
                    <ChecklistItem key={item.field} item={item} />
                  ))}
                </Box>
              )}

              {!isLoading && readiness && !readiness.readyToPublish && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="body2">
                    All fields must be Approved before publishing.
                  </Typography>
                </Alert>
              )}

              <Divider sx={{ mb: 1.5 }} />
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              title={
                !isPublished && readiness && !readiness.readyToPublish
                  ? 'All fields must be approved before publishing'
                  : ''
              }
            >
              <span>
                <Button
                  variant={isPublished ? 'outlined' : 'contained'}
                  color={isPublished ? 'warning' : 'success'}
                  disabled={
                    isSaving ||
                    (!isPublished && (!readiness || !readiness.readyToPublish))
                  }
                  onClick={handlePublishToggle}
                >
                  {isSaving
                    ? 'Saving...'
                    : isPublished
                      ? 'Unpublish'
                      : 'Publish'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Collapse>
    </Card>
  )
}
